import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { patientService } from '../services/patientService'
import { PatientForm } from '../components/PatientForm'
import { PatientFormData, Patient } from '../types/patient'
import './PatientEdit.css'

function transformPatientToFormData(patient: Patient): PatientFormData {
  return {
    firstName: patient.firstName,
    middleName: patient.middleName,
    lastName: patient.lastName,
    dateOfBirth: patient.dateOfBirth,
    gender: patient.gender,
    maritalStatus: patient.maritalStatus,
    email: patient.email,
    phone: patient.phone,
    alternatePhone: patient.alternatePhone,
    street: patient.address?.street,
    city: patient.address?.city,
    state: patient.address?.state,
    postalCode: patient.address?.postalCode,
    country: patient.address?.country,
    emergencyContactName: patient.emergencyContact?.name,
    emergencyContactRelationship: patient.emergencyContact?.relationship,
    emergencyContactPhone: patient.emergencyContact?.phone,
    emergencyContactEmail: patient.emergencyContact?.email,
    bloodType: patient.bloodType,
    allergies: patient.allergies?.join(', '),
    medicalHistory: patient.medicalHistory,
    currentMedications: patient.currentMedications?.join(', '),
    chronicConditions: patient.chronicConditions?.join(', '),
    // Health identifiers
    ppsNumber: patient.ppsNumber,
    gmsNumber: patient.gmsNumber,
    // Coverage & schemes
    patientType: patient.coverage?.patientType,
    gmsReviewDate: patient.coverage?.gms?.reviewDate,
    gmsDoctorNumber: patient.coverage?.gms?.doctorNumber,
    gmsDistanceCode: patient.coverage?.gms?.distanceCode,
    gmsDispensingStatus: patient.coverage?.gms?.dispensingStatus,
    gmsEligibilityStartDate: patient.coverage?.gms?.eligibilityStartDate,
    gmsEligibilityEndDate: patient.coverage?.gms?.eligibilityEndDate,
    doctorVisitCardNumber: patient.coverage?.doctorVisitCard?.number,
    doctorVisitCardReviewDate: patient.coverage?.doctorVisitCard?.reviewDate,
    doctorVisitCardDoctorNumber: patient.coverage?.doctorVisitCard?.doctorNumber,
    doctorVisitCardDistanceCode: patient.coverage?.doctorVisitCard?.distanceCode,
    dpsNumber: patient.coverage?.dps?.number,
    dpsExpiryDate: patient.coverage?.dps?.expiryDate,
    companySchemeAnPost: patient.coverage?.companySchemes?.anPost,
    companySchemeGarda: patient.coverage?.companySchemes?.garda,
    companySchemeOther: patient.coverage?.companySchemes?.other,
    // Lifestyle factors - Smoking
    smokingStatus: patient.smokingStatus || patient.smokingDetails?.status,
    smokingDescription: patient.smokingDetails?.description,
    smokingCessationDetails: patient.smokingDetails?.cessationDetails,
    smokingAmountPerDay: patient.smokingDetails?.amountPerDay,
    smokingYearsSmoking: patient.smokingDetails?.yearsSmoking,
    // Lifestyle factors - Alcohol
    alcoholStatus: patient.alcoholConsumption?.status,
    alcoholDescription: patient.alcoholConsumption?.description,
    alcoholFrequency: patient.alcoholConsumption?.frequency,
    alcoholQuantityPerOccasion: patient.alcoholConsumption?.quantityPerOccasion,
    alcoholType: patient.alcoholConsumption?.type,
    alcoholBingeDrinking: patient.alcoholConsumption?.bingeDrinking,
    alcoholBingeFrequency: patient.alcoholConsumption?.bingeFrequency,
    insuranceProvider: patient.insurance?.provider,
    insurancePolicyNumber: patient.insurance?.policyNumber,
    insuranceGroupNumber: patient.insurance?.groupNumber,
    // Practice registration
    registeredGpName: patient.practiceRegistration?.registeredGpName,
    registeredGpId: patient.practiceRegistration?.registeredGpId,
    usualGpName: patient.practiceRegistration?.usualGpName,
    branchName: patient.practiceRegistration?.branchName,
    registrationDate: patient.practiceRegistration?.registrationDate,
    deregistrationDate: patient.practiceRegistration?.deregistrationDate,
    deregistrationReason: patient.practiceRegistration?.deregistrationReason,
    primaryCareTeamEnrolmentStatus: patient.practiceRegistration?.primaryCareTeamEnrolmentStatus,
    primaryCareTeamEnrolmentDate: patient.practiceRegistration?.primaryCareTeamEnrolmentDate,
    primaryCareTeamDeEnrolmentDate: patient.practiceRegistration?.primaryCareTeamDeEnrolmentDate,
    // Family and Relationships
    familyId: patient.familyId || '',
    guardianId: patient.guardianId || '',
    isDependent: patient.isDependent || false,
    notes: patient.notes,
  }
}

export function PatientEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: patient, isLoading, error } = useQuery({
    queryKey: ['patient', id],
    queryFn: () => patientService.getById(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PatientFormData>) =>
      patientService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', id] })
      queryClient.invalidateQueries({ queryKey: ['patient-summary', id] })
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      navigate(`/patients/${id}`)
    },
  })

  const handleSubmit = async (data: PatientFormData) => {
    await updateMutation.mutateAsync(data)
  }

  const handleCancel = () => {
    navigate(`/patients/${id}`)
  }

  if (isLoading) return <div className="loading">Loading patient...</div>
  if (error) return <div className="error">Error loading patient</div>
  if (!patient) return <div>Patient not found</div>

  const isSecondaryRecord = patient.isPrimary === false || !!patient.primaryPatientId
  if (isSecondaryRecord) {
    return (
      <div className="patient-edit">
        <h1>Edit Patient</h1>
        <div className="error-message">
          This linked record is read-only. Update the primary record instead.
          {patient.primaryPatientId && (
            <>
              {' '}
              <Link to={`/patients/${patient.primaryPatientId}`}>View primary record</Link>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="patient-edit">
      <h1>Edit Patient</h1>
      {updateMutation.isError && (
        <div className="error-message">
          Failed to update patient. Please try again.
        </div>
      )}
      <PatientForm
        initialData={transformPatientToFormData(patient)}
        patientId={id}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={updateMutation.isPending}
      />
    </div>
  )
}

